import React from 'react';
import { useT } from '../context/LanguageContext';

const About = () => {
  const t = useT();
  return (
    <div className="bg-white min-h-screen">
      {/* Page Header */}
      <section className="py-20 bg-[#FAF7F0]">
        <div className="max-w-[800px] mx-auto px-8 text-center">
          <h1
            className="text-5xl font-bold text-[#1A2F4A] mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="about-page-title">
            {t({ fr: 'À propos de Selora', en: 'About Selora' })}
          </h1>
          <div className="flex items-center justify-center gap-4">
            <div className="h-px w-16 bg-[#D4AF37]"></div>
            <span className="text-[#D4AF37] text-lg">⚜</span>
            <div className="h-px w-16 bg-[#D4AF37]"></div>
          </div>
        </div>
      </section>

      {/* Main Content Container */}
      <div className="max-w-[780px] mx-auto px-8 py-16">

        {/* About Selora Section */}
        <section className="mb-20" data-testid="section-about">
          <h2
            className="text-3xl font-bold text-[#1A2F4A] mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            {t({ fr: 'Notre histoire', en: 'About Selora' })}
          </h2>
          <div className="space-y-6 text-[#4A4A4A] leading-relaxed text-lg">
            <p>
              {t({
                fr: "Selora est une plateforme d'apprentissage de la langue française consacrée au développement de la compétence orale chez les apprenants en environnement non francophone. Fondée par des enseignants et des spécialistes des langues, Selora repose sur la conviction qu'un apprentissage linguistique véritable commence par la conversation.",
                en: 'Selora is a French language learning platform dedicated to the development of spoken French competence among learners in non-Francophone environments. Founded by language educators and specialists, Selora is built on the belief that meaningful language learning begins with conversation.',
              })}
            </p>
            <p>
              {t({
                fr: "Notre mission est d'aider les apprenants à parler français avec clarté, confiance et conscience culturelle grâce à une pratique orale structurée et accompagnée.",
                en: 'Our mission is to help learners speak French with clarity, confidence, and cultural awareness through structured and guided oral practice.',
              })}
            </p>
          </div>
        </section>

        {/* Our Mission Section */}
        <section className="mb-20" data-testid="section-mission">
          <h2
            className="text-3xl font-bold text-[#1A2F4A] mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            {t({ fr: 'Notre mission', en: 'Our Mission' })}
          </h2>
          <div className="space-y-6 text-[#4A4A4A] leading-relaxed text-lg">
            <p>
              {t({
                fr: "Notre mission est d'accompagner les apprenants dans le développement d'une capacité communicative fonctionnelle en français, avec une attention particulière à la prononciation, la fluidité et l'interaction orale. La plateforme a été conçue pour répondre aux opportunités limitées de pratique orale rencontrées hors des contextes francophones.",
                en: 'Our mission is to support learners in developing functional communicative ability in French, with particular attention to pronunciation, fluency, and oral interaction. The platform has been designed to respond to the limited opportunities for spoken practice often encountered outside Francophone contexts.',
              })}
            </p>
          </div>
        </section>

        {/* Institutional Profile Section */}
        <section className="mb-20" data-testid="section-institutional">
          <h2 
            className="text-3xl font-bold text-[#1A2F4A] mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Institutional Profile
          </h2>
          <div className="space-y-6 text-[#4A4A4A] leading-relaxed text-lg">
            <p>
              Our team consists of qualified educators, native and near-native French speakers, and academic contributors with professional experience in language teaching and instructional design.
            </p>
            <p>
              We develop structured learning pathways that integrate contemporary pedagogical practices with practical speaking applications, ensuring progressive and systematic development of communicative competence.
            </p>
          </div>
        </section>

        {/* Educational Approach Section */}
        <section className="mb-20" data-testid="section-approach">
          <h2 
            className="text-3xl font-bold text-[#1A2F4A] mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Educational Approach
          </h2>
          <div className="space-y-6 text-[#4A4A4A] leading-relaxed text-lg">
            <p>
              Our instructional framework is grounded in communicative and task-based language teaching principles.
            </p>
            
            {/* Bullet Points */}
            <ul className="space-y-4 pl-1">
              <li className="flex items-start gap-4">
                <span className="text-[#D4AF37] mt-2">•</span>
                <span>Regular oral interaction aligned with learner proficiency levels</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="text-[#D4AF37] mt-2">•</span>
                <span>Exposure to culturally contextualized language use</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="text-[#D4AF37] mt-2">•</span>
                <span>Progressive development of spoken accuracy and coherence</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="text-[#D4AF37] mt-2">•</span>
                <span>A supportive learning environment adapted to non-Francophone learners</span>
              </li>
            </ul>

            <p>
              The focus remains on enabling learners to apply linguistic knowledge in authentic communicative situations rather than limiting instruction to theoretical study.
            </p>
          </div>
        </section>

        {/* Educational Objectives Section */}
        <section className="mb-20" data-testid="section-objectives">
          <h2 
            className="text-3xl font-bold text-[#1A2F4A] mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Educational Objectives
          </h2>
          <div className="space-y-6 text-[#4A4A4A] leading-relaxed text-lg">
            <p>
              The primary objective of the platform is to enable learners to communicate in French with clarity and confidence in everyday, academic, and professional contexts.
            </p>
            <p>
              Through sustained oral practice and structured feedback, Selora seeks to promote autonomous language use and long-term communicative competence.
            </p>
          </div>
        </section>

        {/* Why Choose Selora Section */}
        <section className="mb-20" data-testid="section-why-choose">
          <h2 
            className="text-3xl font-bold text-[#1A2F4A] mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Why Choose Selora
          </h2>
          <div className="space-y-6 text-[#4A4A4A] leading-relaxed text-lg">
            <p>
              Many French learning programs emphasize grammatical knowledge while offering limited opportunities for real conversation. Selora was created to address this gap.
            </p>
            <p>
              By prioritizing structured speaking interaction, guided feedback, and consistent oral exposure, we help learners develop practical communicative confidence from the earliest stages of learning.
            </p>
            <p>
              Our programs are designed to be flexible and accessible, supporting academic, professional, and personal learning goals while maintaining a strong focus on real-world language use.
            </p>
          </div>
        </section>

        {/* Our Goal Section - Conclusion */}
        <section className="mb-8 pt-8 border-t border-[#D4AF37]/20" data-testid="section-goal">
          <h2 
            className="text-3xl font-bold text-[#1A2F4A] mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Our Goal
          </h2>
          <div className="space-y-6 text-[#4A4A4A] leading-relaxed text-lg">
            <p>
              Our goal is to help learners speak French with confidence and natural ease. We focus on developing the ability to think, express ideas, and communicate in French without hesitation.
            </p>
            <p className="italic text-[#1A2F4A]">
              French, for us, is not simply a subject to be studied, but a living language to be experienced, shared, and used to connect with the world.
            </p>
          </div>
        </section>

      </div>

      {/* Footer Transition Spacing */}
      <div className="h-24 bg-[#FAF7F0]"></div>

      {/* Footer */}
      <footer className="bg-[#FAF7F0] border-t border-[#D4AF37]/30 py-12">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-white border-2 border-[#D4AF37] shadow flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_parlezmoifrench/artifacts/25ispvfj_selora%20logo-removebg.jpg"
                  alt="Selora Logo"
                  className="w-14 h-14 object-cover scale-125"
                />
              </div>
              <div>
                <span className="text-xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  SELORA
                </span>
                <p className="text-xs text-[#6B4E37]">French Academy</p>
              </div>
            </div>
            <p className="text-[#6B4E37] text-sm">
              © 2026 Selora French Academy. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
